import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { NgbTooltip, Placement } from '@ng-bootstrap/ng-bootstrap';
import { TextEllipsisAppendDirective } from './text-ellipsis-append.directive';
import { TextEllipsisPrependDirective } from './text-ellipsis-prepend.directive';
import { TextEllipsisResizeObserverService } from './text-ellipsis-resize-observer.service';

const DEFAULT_HOST_CLASSES = 'mbs-text-ellipsis row g-0 flex-nowrap mw-100';

@Component({
  selector: 'mbs-text-ellipsis',
  templateUrl: './text-ellipsis.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgbTooltip, NgTemplateOutlet],
  host: {
    '[class]': 'innerHostClasses()'
  }
})
export class TextEllipsisComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly resizeObserver = inject(TextEllipsisResizeObserverService);

  public readonly autoClose = input<boolean>(false);
  public readonly closeDelay = input<number>(0);
  public readonly container = input<'body' | null>('body');
  public readonly disableTooltip = input<boolean>(false);
  public readonly openDelay = input<number>(0);
  public readonly placement = input<Placement | Array<Placement> | string>('top-left');
  public readonly tooltip = input<string | TemplateRef<any>>(null);
  public readonly tooltipClass = input<string>(null);
  public readonly triggers = input<string>('hover focus');
  public readonly hostClasses = input<string>('', { alias: 'class' });

  protected readonly innerHostClasses = computed<string>(() => DEFAULT_HOST_CLASSES + ' ' + this.hostClasses());
  protected readonly isTruncated = signal<boolean>(false);

  protected append = contentChild(TextEllipsisAppendDirective, { read: TextEllipsisAppendDirective, descendants: true });
  protected prepend = contentChild(TextEllipsisPrependDirective, { read: TextEllipsisPrependDirective, descendants: true });

  private contentSpan = viewChild('content', { read: ElementRef<HTMLSpanElement> });

  constructor() {
    afterNextRender(() => {
      const content = this.contentSpan().nativeElement;

      this.resizeObserver.observe(content, () => this.isTruncated.set(content.scrollWidth > content.clientWidth));
      this.destroyRef.onDestroy(() => this.resizeObserver.unobserve(content));
    });
  }
}
